import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import BackToTop from "@/components/base/BackToTop";
import { getComicById, getRelatedComics, type Comic, type Chapter } from "@/mocks/comics";
import { categories } from "@/mocks/categories";

function getCategoryName(slug: string): string {
  const cat = categories.find((c) => c.slug === slug);
  return cat ? cat.name : slug;
}

export default function ArticlePage() {
  const { id } = useParams<{ id: string }>();
  const [comic, setComic] = useState<Comic | undefined>();
  const [relatedComics, setRelatedComics] = useState<Comic[]>([]);
  const [selectedChapter, setSelectedChapter] = useState<Chapter | null>(null);
  const [showAllChapters, setShowAllChapters] = useState(false);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
    if (id) {
      const found = getComicById(Number(id));
      setComic(found);
      setSelectedChapter(null);
      setShowAllChapters(false);
      if (found) {
        setRelatedComics(getRelatedComics(found));
      }
    }
  }, [id]);

  if (!comic) {
    return (
      <>
        <Navbar />
        <main className="pt-16 min-h-screen flex items-center justify-center bg-background-50">
          <div className="text-center">
            <div className="w-20 h-20 mx-auto mb-4 flex items-center justify-center rounded-full bg-background-100">
              <i className="ri-emotion-sad-line text-3xl text-foreground-400"></i>
            </div>
            <h2 className="text-xl font-heading font-bold text-foreground-900 mb-2">漫画未找到</h2>
            <p className="text-sm text-foreground-500 mb-4">抱歉，您要找的漫画不存在或已下架</p>
            <Link
              to="/"
              className="inline-flex px-6 py-2 bg-primary-500 hover:bg-primary-600 text-white text-sm font-medium rounded-full transition-colors cursor-pointer"
            >
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const articleSchema = {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: comic.title,
    author: { "@type": "Person", name: comic.author },
    description: comic.description,
    image: comic.cover,
    datePublished: comic.chapters[0]?.updateTime || comic.updateTime,
    dateModified: comic.updateTime,
    publisher: {
      "@type": "Organization",
      name: "韩漫在线",
    },
    breadcrumb: {
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "首页", item: typeof window !== "undefined" ? window.location.origin + "/" : "" },
        { "@type": "ListItem", position: 2, name: "漫画分类", item: typeof window !== "undefined" ? window.location.origin + "/category/all" : "" },
        { "@type": "ListItem", position: 3, name: getCategoryName(comic.category), item: typeof window !== "undefined" ? window.location.origin + "/category/" + comic.category : "" },
        { "@type": "ListItem", position: 4, name: comic.title },
      ],
    },
  };

  const displayedChapters = showAllChapters ? comic.chapters : comic.chapters.slice(0, 6);

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(articleSchema) }}
      />
      <Navbar />
      <main className="pt-16">
        <nav className="bg-background-100 border-b border-background-200">
          <div className="max-w-[1280px] mx-auto px-4 md:px-6 py-3">
            <div className="flex items-center gap-2 text-xs text-foreground-500 flex-wrap">
              <Link to="/" className="hover:text-primary-500 transition-colors cursor-pointer">首页</Link>
              <i className="ri-arrow-right-s-line"></i>
              <Link to="/category/all" className="hover:text-primary-500 transition-colors cursor-pointer">漫画分类</Link>
              <i className="ri-arrow-right-s-line"></i>
              <Link to={`/category/${comic.category}`} className="hover:text-primary-500 transition-colors cursor-pointer">
                {getCategoryName(comic.category)}
              </Link>
              <i className="ri-arrow-right-s-line"></i>
              <span className="text-foreground-900">{comic.title}</span>
            </div>
          </div>
        </nav>

        {selectedChapter ? (
          <section className="py-8 md:py-12 bg-background-50 min-h-screen">
            <div className="max-w-[800px] mx-auto px-4 md:px-6">
              <div className="flex items-center justify-between mb-6">
                <button
                  onClick={() => setSelectedChapter(null)}
                  className="flex items-center gap-1 text-sm text-foreground-600 hover:text-primary-500 transition-colors cursor-pointer"
                >
                  <i className="ri-arrow-left-line"></i> 返回详情
                </button>
                <h2 className="text-base font-heading font-bold text-foreground-900">
                  {comic.title} - {selectedChapter.title}
                </h2>
              </div>

              <div className="bg-background-100 rounded-lg p-8 md:p-12 min-h-[600px] flex flex-col items-center justify-center border border-background-200/70">
                <div className="w-20 h-20 mb-6 flex items-center justify-center rounded-full bg-primary-100">
                  <i className="ri-book-read-line text-3xl text-primary-500"></i>
                </div>
                <h3 className="text-lg font-heading font-bold text-foreground-900 mb-2">
                  {selectedChapter.title}
                </h3>
                <p className="text-sm text-foreground-500 mb-8 text-center max-w-md">
                  下拉式漫画阅读区域，高清画质呈现。向下滚动即可连续阅读本章全部内容。
                </p>
                <div className="w-full max-w-lg space-y-3">
                  {Array.from({ length: 4 }).map((_, i) => (
                    <div
                      key={i}
                      className="w-full h-64 bg-background-200/70 rounded-lg flex items-center justify-center"
                    >
                      <span className="text-sm text-foreground-400">漫画画面 {i + 1}</span>
                    </div>
                  ))}
                </div>

                <div className="flex items-center gap-3 mt-8">
                  <button
                    onClick={() => {
                      const idx = comic.chapters.findIndex((c) => c.id === selectedChapter.id);
                      if (idx > 0) setSelectedChapter(comic.chapters[idx - 1]);
                    }}
                    disabled={comic.chapters.findIndex((c) => c.id === selectedChapter.id) === 0}
                    className="px-4 py-2 bg-background-200 hover:bg-background-300 text-foreground-700 text-sm rounded-full transition-colors disabled:opacity-40 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer"
                  >
                    <i className="ri-arrow-left-line mr-1"></i>上一话
                  </button>
                  <button
                    onClick={() => {
                      const idx = comic.chapters.findIndex((c) => c.id === selectedChapter.id);
                      if (idx < comic.chapters.length - 1) setSelectedChapter(comic.chapters[idx + 1]);
                    }}
                    disabled={comic.chapters.findIndex((c) => c.id === selectedChapter.id) === comic.chapters.length - 1}
                    className="px-4 py-2 bg-primary-500 hover:bg-primary-600 text-white text-sm rounded-full transition-colors disabled:opacity-40 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer"
                  >
                    下一话<i className="ri-arrow-right-line ml-1"></i>
                  </button>
                </div>
              </div>
            </div>
          </section>
        ) : (
          <>
            <section className="py-8 md:py-12 bg-background-50">
              <div className="max-w-[1280px] mx-auto px-4 md:px-6">
                <div className="flex flex-col lg:flex-row gap-8">
                  <div className="w-full lg:w-[280px] flex-shrink-0">
                    <div className="sticky top-24">
                      <div className="rounded-lg overflow-hidden mb-4 aspect-[5/7]">
                        <img
                          src={comic.cover}
                          alt={comic.title}
                          className="w-full h-full object-cover object-top"
                        />
                      </div>
                      <button className="w-full px-6 py-3 bg-primary-500 hover:bg-primary-600 text-white text-sm font-medium rounded-full transition-colors mb-3 whitespace-nowrap cursor-pointer">
                        开始阅读第一话
                      </button>
                      <button className="w-full px-6 py-3 bg-background-100 hover:bg-background-200 text-foreground-700 text-sm font-medium rounded-full transition-colors whitespace-nowrap cursor-pointer border border-background-200">
                        <i className="ri-bookmark-line mr-1"></i>加入收藏
                      </button>
                    </div>
                  </div>

                  <div className="flex-1 min-w-0">
                    <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 mb-2">
                      {comic.title}
                    </h1>
                    <div className="flex flex-wrap items-center gap-3 mb-4">
                      <span className="text-sm text-foreground-500">{comic.author} 著</span>
                      <span className="w-1 h-1 rounded-full bg-foreground-300"></span>
                      <span className="text-sm text-primary-500 font-medium">{comic.rating} 分</span>
                      <span className="w-1 h-1 rounded-full bg-foreground-300"></span>
                      <span className={`text-sm font-medium ${comic.status === "连载中" ? "text-secondary-600" : "text-foreground-500"}`}>
                        {comic.status}
                      </span>
                      <span className="w-1 h-1 rounded-full bg-foreground-300"></span>
                      <span className="text-sm text-foreground-500">
                        {comic.views >= 10000
                          ? `${(comic.views / 10000).toFixed(1)}万阅读`
                          : `${comic.views}阅读`}
                      </span>
                    </div>

                    <div className="flex flex-wrap items-center gap-2 mb-4">
                      <Link
                        to={`/category/${comic.category}`}
                        className="px-3 py-1 bg-secondary-100 text-secondary-700 text-xs rounded-full cursor-pointer"
                      >
                        {getCategoryName(comic.category)}
                      </Link>
                      {comic.tags.map((tag) => (
                        <span key={tag} className="px-3 py-1 bg-accent-100 text-accent-700 text-xs rounded-full">
                          {tag}
                        </span>
                      ))}
                    </div>

                    <div className="bg-background-100 rounded-lg p-4 md:p-5 mb-6 border border-background-200/70">
                      <h3 className="text-sm font-semibold text-foreground-900 mb-2">作品简介</h3>
                      <p className="text-sm text-foreground-600 leading-relaxed">{comic.description}</p>
                    </div>

                    <div className="mb-6">
                      <div className="flex items-center justify-between mb-3">
                        <h3 className="text-sm font-semibold text-foreground-900">
                          章节列表（共{comic.chapters.length}话）
                        </h3>
                        {comic.chapters.length > 6 && (
                          <button
                            onClick={() => setShowAllChapters(!showAllChapters)}
                            className="text-xs text-primary-500 hover:text-primary-600 cursor-pointer"
                          >
                            {showAllChapters ? "收起" : "查看全部"}
                          </button>
                        )}
                      </div>
                      <div className="flex flex-col gap-1">
                        {displayedChapters.map((chapter, idx) => (
                          <button
                            key={chapter.id}
                            onClick={() => setSelectedChapter(chapter)}
                            className="flex items-center justify-between px-4 py-3 rounded-lg bg-background-100 hover:bg-primary-50 border border-background-200/70 hover:border-primary-200 transition-all duration-200 cursor-pointer group"
                          >
                            <span className="text-sm text-foreground-700 group-hover:text-primary-600 transition-colors">
                              {chapter.title}
                            </span>
                            <span className="text-xs text-foreground-400">{chapter.updateTime}</span>
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </section>

            {relatedComics.length > 0 && (
              <section className="py-10 md:py-14 bg-background-100">
                <div className="max-w-[1280px] mx-auto px-4 md:px-6">
                  <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 mb-6">
                    相关推荐
                  </h2>
                  <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4 md:gap-5">
                    {relatedComics.map((rel) => (
                      <Link
                        key={rel.id}
                        to={`/article/${rel.id}`}
                        className="group cursor-pointer"
                      >
                        <div className="relative overflow-hidden rounded-lg mb-3 aspect-[5/7]">
                          <img
                            src={rel.cover}
                            alt={rel.title}
                            className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                          />
                          <div className="absolute top-2 left-2">
                            <span className="px-2 py-0.5 bg-primary-500 text-white text-xs font-medium rounded-full">
                              {rel.rating}
                            </span>
                          </div>
                        </div>
                        <h3 className="text-sm font-medium text-foreground-900 group-hover:text-primary-500 transition-colors line-clamp-1 mb-1">
                          {rel.title}
                        </h3>
                        <p className="text-xs text-foreground-500">{rel.author}</p>
                      </Link>
                    ))}
                  </div>
                </div>
              </section>
            )}
          </>
        )}
      </main>
      <Footer />
      <BackToTop />
    </>
  );
}